/**
 * DatabaseInstancesList — Instancias de base de datos (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), borrado con `window.confirm` y el copiado
 * con dos `window.prompt` seguidos (nombre y método) — imposible de usar con
 * teclado y sin ver qué se estaba eligiendo.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** y el borrado usa el diálogo único. El botón **Copiar**
 * del estándar abre un **diálogo** que pide nombre y método de copia.
 *
 * Botones propios:
 *  · **Ver sus bases de datos** — diálogo con nombre, tamaño, entorno y estado
 *    (se piden a `GET /apps/database-instances/<id>/databases`).
 *  · **Ver el host** — a la ficha del servidor físico o de la VM que la aloja.
 *  · **Ver el servicio** y **Ver en el Diseñador**.
 *  · **Ver la instancia de la que copia** — si es una copia.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CircleStackIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ServerStackIcon,
  WrenchScrewdriverIcon,
  ShareIcon,
  ArrowUturnLeftIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import DatabaseInstanceForm from './DatabaseInstanceForm';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';

const STATUS_META = {
  online:      { label: 'Online',        badge: 'list-badge--on' },
  offline:     { label: 'Offline',       badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
};

const COPY_METHODS = [
  { value: 'native',         label: 'Réplica del propio motor (streaming / Always On / log shipping)' },
  { value: 'backup_restore', label: 'Restaurar un backup del propio SQL' },
  { value: 'vm_clone',       label: 'Clonar la máquina entera' },
];

const EMPTY_INSTANCE = {
  name: '', service_id: '', dbms: '', version: '', port: '', role: 'standalone',
  status: 'online', host_server_id: '', host_vm_id: '', data_dir: '', notes: '',
};

export default function DatabaseInstancesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [instances, setInstances] = useState([]);
  const [meta, setMeta]           = useState({ dbms_types: [], db_roles: [], db_statuses: [] });
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');

  const [filterDbms, setFilterDbms]         = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterRole, setFilterRole]         = useState('');
  const [filterService, setFilterService]   = useState('');
  const [filterHostType, setFilterHostType] = useState('');
  const [filterDbs, setFilterDbs]           = useState('');
  const [filterReplication, setFilterReplication] = useState('');
  const [filterCopy, setFilterCopy]         = useState('');
  const [groupMode, setGroupMode]           = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [hlId, setHlId]               = useState(null);

  /* Diálogos */
  const [dbsOf, setDbsOf]               = useState(null);
  const [dbs, setDbs]                   = useState([]);
  const [dbsLoading, setDbsLoading]     = useState(false);
  const [copyOf, setCopyOf]             = useState(null);
  const [copyForm, setCopyForm]         = useState({ name: '', copy_method: 'native' });
  const [copySaving, setCopySaving]     = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [metaResp, list, svcs] = await Promise.all([
        api.getAppsMeta(companyId),
        api.getDatabaseInstances(companyId),
        api.getServices(companyId),
      ]);
      setMeta(metaResp || { dbms_types: [], db_roles: [], db_statuses: [] });
      setInstances(Array.isArray(list) ? list : []);
      setServices(Array.isArray(svcs) ? svcs : (svcs.services || []));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las bases de datos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !instances.length) return undefined;
    const id = Number(raw);
    const target = instances.find(i => i.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`dbinst-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, instances, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (i) => { setInitialMode('view');   setSelected(i); };
  const openEdit   = (i) => { setInitialMode('edit');   setSelected(i); };
  const openCreate = ()  => { setInitialMode('create'); setSelected({ ...EMPTY_INSTANCE }); };
  const closeForm  = ()  => { setSelected(null); load(); };
  useAutoOpen(instances, openEdit);

  const handleDelete = async (item) => {
    try {
      await api.deleteDatabaseInstance(item.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  /* ── bases dentro de una instancia ──────────────────────────────────────── */
  const openDbs = async (item) => {
    setDbsOf(item);
    setDbs([]);
    setDbsLoading(true);
    try {
      const res = await api.getInstanceDatabases(item.id, companyId);
      setDbs(res.data || res || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las bases de datos');
      setDbsOf(null);
    } finally {
      setDbsLoading(false);
    }
  };

  /* ── copiar instancia ───────────────────────────────────────────────────── */
  const openCopy = (item) => {
    const base = item.name || `${item.dbms}${item.version ? ` ${item.version}` : ''}`;
    setCopyOf(item);
    setCopyForm({ name: `${base} (copia)`, copy_method: 'native' });
  };

  const handleConfirmCopy = async () => {
    if (!copyOf || !copyForm.name.trim()) return;
    setCopySaving(true);
    try {
      await api.copyDatabaseInstance(copyOf.id, {
        name: copyForm.name.trim(),
        copy_method: copyForm.copy_method,
      }, companyId);
      setCopyOf(null);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo copiar');
      setCopyOf(null);
    } finally {
      setCopySaving(false);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const serviceLabel = useCallback(
    (id) => services.find(s => s.id === id)?.display_name || services.find(s => s.id === id)?.name || id,
    [services],
  );

  const dbmsOptions = useMemo(() => {
    const seen = new Map();
    instances.forEach(i => { if (i.dbms) seen.set(i.dbms, i.dbms); });
    (meta.dbms_types || []).forEach(d => {
      const value = typeof d === 'string' ? d : d.value;
      const label = typeof d === 'string' ? d : (d.label || d.value);
      if (value && !seen.has(value)) seen.set(value, label);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [instances, meta.dbms_types]);

  const serviceOptions = useMemo(() => {
    const seen = new Map();
    instances.forEach(i => {
      if (i.service_id) seen.set(String(i.service_id), i.service_name || serviceLabel(i.service_id));
    });
    return [...seen.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [instances, serviceLabel]);

  const roleOptions = useMemo(() => {
    const seen = new Map();
    instances.forEach(i => { if (i.role) seen.set(i.role, i.role); });
    (meta.db_roles || []).forEach(r => {
      const value = typeof r === 'string' ? r : r.value;
      const label = typeof r === 'string' ? r : (r.label || r.value);
      if (value && !seen.has(value)) seen.set(value, label);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [instances, meta.db_roles]);

  const hostLabel = (i) => i.host_vm_name || i.host_server_name || null;
  const hostPath = (i) => {
    if (i.host_vm_id) return `/virt/vms?destacar=${i.host_vm_id}&abrir=1`;
    if (i.host_server_id) return `/infrastructure/servers?destacar=${i.host_server_id}&abrir=1`;
    return null;
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return instances.filter((i) => {
      if (filterDbms && i.dbms !== filterDbms) return false;
      if (filterStatus && i.status !== filterStatus) return false;
      if (filterRole && i.role !== filterRole) return false;
      if (filterService && String(i.service_id || '') !== String(filterService)) return false;
      if (filterHostType === 'vm' && !i.host_vm_id) return false;
      if (filterHostType === 'server' && !i.host_server_id) return false;
      if (filterDbs === 'with' && !(i.databases_count > 0)) return false;
      if (filterDbs === 'without' && i.databases_count > 0) return false;
      if (filterReplication === 'with' && !i.replication_target) return false;
      if (filterReplication === 'without' && i.replication_target) return false;
      if (filterCopy === 'copy' && !i.source_instance_id) return false;
      if (filterCopy === 'original' && i.source_instance_id) return false;
      if (!term) return true;
      return [
        i.name, i.dbms, i.version, i.notes, i.role, i.data_dir, i.replication_target,
        i.service_name, serviceLabel(i.service_id), i.host_vm_name, i.host_server_name,
        (i.databases || []).map(d => d.name).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [instances, search, filterDbms, filterStatus, filterRole, filterService,
    filterHostType, filterDbs, filterReplication, filterCopy, serviceLabel]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navDatabases = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('dbinst.name', 'Instancia'),
        sortable: true,
        render: (i) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{i.name || `${i.dbms || ''} ${i.version || ''}`.trim()}</span>
            {i.source_instance_name && (
              <span className="list-hint">
                {tr('dbinst.copy_of', 'Copia de')} {i.source_instance_name}
                {i.copy_method ? ` · ${(COPY_METHODS.find(m => m.value === i.copy_method) || {}).label || i.copy_method}` : ''}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'service',
        label: tr('dbinst.service', 'Servicio'),
        sortable: true,
        sortValue: (i) => i.service_name || '',
        render: (i) => (i.service_id
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/services?destacar=${i.service_id}&abrir=1`);
              }}
              title={tr('dbinst.view_service', 'Ver el servicio')}
            >
              {i.service_name || serviceLabel(i.service_id)}
            </button>
          )
          : dash),
      },
      {
        key: 'host',
        label: tr('dbinst.host', 'Host'),
        sortable: true,
        sortValue: (i) => hostLabel(i) || '',
        render: (i) => {
          const label = hostLabel(i);
          if (!label) return dash;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{label}</span>
              <span className="list-hint">
                {i.host_vm_id ? tr('dbinst.host_vm', 'Máquina virtual') : tr('dbinst.host_server', 'Servidor físico')}
              </span>
            </span>
          );
        },
      },
      {
        key: 'dbms',
        label: 'DBMS',
        sortable: true,
        sortValue: (i) => `${i.dbms || ''} ${i.version || ''}`,
        render: (i) => (
          <span className="list-badge">{i.dbms || dash}{i.version ? ` ${i.version}` : ''}</span>
        ),
      },
      {
        key: 'port',
        label: tr('dbinst.port', 'Puerto'),
        sortable: true,
        render: (i) => (i.port ? <span className="list-code">{i.port}</span> : dash),
      },
      {
        key: 'databases_count',
        label: tr('dbinst.databases', 'Bases'),
        sortable: true,
        sortValue: (i) => i.databases_count ?? 0,
        render: (i) => (
          <button
            type="button"
            className="list-badge"
            style={{ cursor: i.databases_count ? 'pointer' : 'default' }}
            disabled={!i.databases_count}
            onClick={(event) => { if (!i.databases_count) return; event.stopPropagation(); openDbs(i); }}
            title={tr('dbinst.view_databases', 'Ver sus bases de datos')}
          >
            {i.databases_count || 0}
          </button>
        ),
      },
      {
        key: 'role',
        label: tr('dbinst.role', 'Rol'),
        sortable: true,
        render: (i) => (i.role ? <span className="list-badge">{i.role}</span> : dash),
      },
      {
        key: 'replication',
        label: tr('dbinst.replication', 'Réplica'),
        render: (i) => (i.replication_target
          ? <span className="list-code" title={i.replication_config || ''}>{i.replication_target}</span>
          : dash),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (i) => i.status || 'online',
        render: (i) => {
          const meta_ = STATUS_META[i.status] || { label: i.status || '—', badge: '' };
          return <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (i) => i.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, serviceLabel]);

  const defaultVisible = ['name', 'service', 'host', 'dbms', 'port', 'databases_count', 'role', 'status'];

  const filters = [
    {
      key: 'dbms', label: 'DBMS', allLabel: tr('dbinst.dbms_all', 'Cualquier motor'),
      value: filterDbms, onChange: setFilterDbms, options: dbmsOptions,
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: (meta.db_statuses || Object.keys(STATUS_META)).map(s => ({
        value: s, label: (STATUS_META[s] || {}).label || s,
      })),
    },
    {
      key: 'role', label: tr('dbinst.role', 'Rol'), allLabel: tr('dbinst.role_all', 'Cualquier rol'),
      value: filterRole, onChange: setFilterRole, options: roleOptions,
    },
    {
      key: 'service', label: tr('dbinst.service', 'Servicio'), allLabel: tr('dbinst.service_all', 'Cualquier servicio'),
      value: filterService, onChange: setFilterService, options: serviceOptions,
    },
    {
      key: 'hostType', label: tr('dbinst.host_type', 'Tipo de host'), allLabel: tr('dbinst.host_all', 'Cualquier host'),
      value: filterHostType, onChange: setFilterHostType,
      options: [
        { value: 'server', label: tr('dbinst.host_server', 'Servidor físico') },
        { value: 'vm', label: tr('dbinst.host_vm', 'Máquina virtual') },
      ],
    },
    {
      key: 'dbs', label: tr('dbinst.databases', 'Bases'), allLabel: tr('dbinst.dbs_all', 'Con o sin bases'),
      value: filterDbs, onChange: setFilterDbs,
      options: [
        { value: 'with', label: tr('dbinst.dbs_with', 'Con bases dentro') },
        { value: 'without', label: tr('dbinst.dbs_without', 'Sin bases dentro') },
      ],
    },
    {
      key: 'replication', label: tr('dbinst.replication', 'Réplica'), allLabel: tr('dbinst.rep_all', 'Con o sin réplica'),
      value: filterReplication, onChange: setFilterReplication,
      options: [
        { value: 'with', label: tr('dbinst.rep_with', 'Con réplica configurada') },
        { value: 'without', label: tr('dbinst.rep_without', 'Sin réplica') },
      ],
    },
    {
      key: 'copy', label: tr('dbinst.origin', 'Origen'), allLabel: tr('dbinst.origin_all', 'Originales y copias'),
      value: filterCopy, onChange: setFilterCopy,
      options: [
        { value: 'original', label: tr('dbinst.original', 'Solo instancias originales') },
        { value: 'copy', label: tr('dbinst.copy', 'Solo copias de otra') },
      ],
    },
    {
      key: 'group', label: tr('dbinst.group', 'Agrupar por'), allLabel: tr('dbinst.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'dbms', label: 'DBMS' },
        { value: 'host', label: tr('dbinst.host', 'Host') },
        { value: 'service', label: tr('dbinst.service', 'Servicio') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'dbms') return (i) => i.dbms || tr('list.no_value', 'Sin valor');
    if (groupMode === 'host') return (i) => hostLabel(i) || tr('dbinst.no_host', 'Sin host');
    if (groupMode === 'service') return (i) => i.service_name || serviceLabel(i.service_id) || tr('dbinst.no_service', 'Sin servicio');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, serviceLabel]);

  const subtitle = `${instances.length} ${tr('dbinst.plural', 'instancias')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(i => ({
    'Instancia': i.name || '',
    'Servicio': i.service_name || serviceLabel(i.service_id),
    'Host': hostLabel(i) || '',
    'Tipo de host': i.host_vm_id ? 'VM' : i.host_server_id ? 'Servidor' : '',
    'DBMS': i.dbms || '',
    'Versión': i.version || '',
    'Puerto': i.port ?? '',
    'Rol': i.role || '',
    'Réplica hacia': i.replication_target || '',
    'Copia de': i.source_instance_name || '',
    'Método de copia': (COPY_METHODS.find(m => m.value === i.copy_method) || {}).label || '',
    'Bases dentro': i.databases_count ?? 0,
    'Bases (detalle)': (i.databases || []).map(d => d.name).join(', '),
    'Estado': (STATUS_META[i.status] || {}).label || i.status || '',
    'Notas': i.notes || '',
  })), [filtered, serviceLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Tarjetas: lo esencial de la instancia, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((i) => {
          const st = STATUS_META[i.status] || { label: i.status || '—', badge: '' };
          return (
            <div
              key={i.id}
              id={`dbinst-card-${i.id}`}
              className={`list-card${i.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(i)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(i); }}
              title={`${tr('action.view', 'Ver')} ${i.name || i.dbms}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{i.name || `${i.dbms || ''} ${i.version || ''}`.trim()}</div>
                  {i.service_name && <span className="list-hint">{i.service_name}</span>}
                </div>
                <span className={`list-badge ${st.badge}`}>{st.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">{i.dbms || '—'}{i.port ? ` :${i.port}` : ''}</span>
                {hostLabel(i) && <span>{hostLabel(i)}</span>}
                {i.role && <span className="list-badge">{i.role}</span>}
                <span className="list-badge">{i.databases_count || 0} {tr('dbinst.databases', 'bases')}</span>
                {i.replication_target && <span className="list-hint">→ {i.replication_target}</span>}
              </div>

              <div className="list-card-actions">{rowActions(i)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const DB_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada instancia'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('database_instances', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('dbinst.select_company', 'Selecciona una empresa para ver las bases de datos.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {selected !== null && (
        <DatabaseInstanceForm
          instance={selected && selected.id ? selected : null}
          companyId={companyId}
          meta={meta}
          services={services}
          initialMode={initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navDatabases}
          navIndex={selected && selected.id ? navDatabases.findIndex(d => String(d.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSaved={(created) => { load(); if (created?.id) setSelected(created); }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={CircleStackIcon}
          title={tr('dbinst.title', 'Instancias de base de datos')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('dbinst.new', 'Nueva instancia')}
          createTitle={tr('dbinst.new_hint', 'Instancia de un motor (SQL Server, PostgreSQL, MySQL…)')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('dbinst.search', 'Buscar por nombre, servicio, host, motor, base…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="bases-de-datos"
          exportSheetName="Bases de datos"

          columns={columns}
          columnStorageKey="cmdb_cols_database_instances"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(i) => i.id}
          rowId={(i) => `dbinst-row-${i.id}`}
          rowClassName={(i) => (i.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(i) => `${tr('action.view', 'Ver')} ${i.name || i.dbms}`}
          groupBy={groupBy}

          views={DB_VIEWS}
          listKey="database_instances"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando instancias…')}
          error={error}

          empty={{
            icon: '🗄️',
            title: instances.length === 0
              ? tr('dbinst.empty', 'No hay instancias')
              : tr('msg.no_results', 'Ninguna instancia coincide con los filtros'),
            subtitle: instances.length === 0
              ? tr('dbinst.empty_hint', 'Crea la primera instancia y cuélgala de su servicio y de su host.')
              : undefined,
            action: instances.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('dbinst.new', 'Nueva instancia')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (i) => (
              <>
                {/* Ver sus bases de datos */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(i.databases_count > 0)}
                  onClick={() => openDbs(i)}
                  title={tr('dbinst.view_databases', 'Ver sus bases de datos')}
                >
                  <TableCellsIcon />
                </button>
                {/* Ver el host */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!hostPath(i)}
                  onClick={() => { const p = hostPath(i); if (p) navigate(p); }}
                  title={hostLabel(i)
                    ? `${tr('dbinst.view_host', 'Ver el host')}: ${hostLabel(i)}`
                    : tr('dbinst.no_host', 'Sin host')}
                >
                  <ServerStackIcon />
                </button>
                {/* Ver el servicio */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!i.service_id}
                  onClick={() => navigate(`/services?destacar=${i.service_id}&abrir=1`)}
                  title={tr('dbinst.view_service', 'Ver el servicio')}
                >
                  <WrenchScrewdriverIcon />
                </button>
                {/* Ver la instancia de la que copia */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!i.source_instance_id}
                  onClick={() => navigate(`/apps/databases?destacar=${i.source_instance_id}&abrir=1`)}
                  title={i.source_instance_name
                    ? `${tr('dbinst.view_source', 'Ver la instancia de la que copia')}: ${i.source_instance_name}`
                    : tr('dbinst.no_source', 'No es una copia')}
                >
                  <ArrowUturnLeftIcon />
                </button>
                {/* Ver en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!i.service_id}
                  onClick={() => navigate(`/services/designer?service=${i.service_id}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('dbinst.delete_title', 'Eliminar instancia'),
            deleteMessage: (i) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')}{' '}
                <strong>{i.name || i.dbms}</strong>?{' '}
                {i.databases_count > 0
                  ? tr('dbinst.delete_used', `Se eliminarán también las ${i.databases_count} base(s) que contiene.`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: bases dentro de la instancia ══ */}
      {dbsOf && (
        <Dialog
          title={`${tr('dbinst.databases_of', 'Bases de')} ${dbsOf.name || dbsOf.dbms}`}
          icon={<TableCellsIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          onClose={() => setDbsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setDbsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {dbsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!dbsLoading && dbs.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('dbinst.no_databases', 'Esta instancia no tiene bases declaradas.')}
            </p>
          )}
          {!dbsLoading && dbs.length > 0 && (
            <div>
              {dbs.map(d => (
                <div
                  key={d.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '6px 0', borderBottom: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontWeight: 600, minWidth: 160 }}>{d.name}</span>
                  <span className="list-badge">{d.environment_label || d.environment || '—'}</span>
                  <span className="list-code">{d.size_gb != null ? `${d.size_gb} GB` : '—'}</span>
                  {d.purpose && <span className="list-hint">{d.purpose}</span>}
                  <span className="list-badge" style={{ marginLeft: 'auto' }}>{d.status || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </Dialog>
      )}

      {/* ══ Diálogo: copiar instancia ══ */}
      {copyOf && (
        <Dialog
          title={tr('dbinst.copy_title', 'Copiar instancia')}
          icon={<CircleStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={520}
          onClose={() => setCopyOf(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setCopyOf(null)} disabled={copySaving}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={handleConfirmCopy}
                disabled={copySaving || !copyForm.name.trim()}
              >
                {copySaving ? tr('msg.saving', 'Copiando…') : tr('dbinst.copy_confirm', 'Crear la copia')}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label className="list-hint">
              {tr('dbinst.copy_name', 'Nombre de la copia')}
              <input
                className="list-search"
                style={{ width: '100%', marginTop: 4 }}
                value={copyForm.name}
                onChange={(e) => setCopyForm(f => ({ ...f, name: e.target.value }))}
                placeholder={`${(copyOf.name || copyOf.dbms || '')} (copia)`}
              />
            </label>
            <label className="list-hint">
              {tr('dbinst.copy_method', 'Cómo se hace la copia')}
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={copyForm.copy_method}
                onChange={(e) => setCopyForm(f => ({ ...f, copy_method: e.target.value }))}
              >
                {COPY_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('dbinst.copy_hint', 'La copia hereda motor y versión. El puerto se deja vacío para no chocar con el original.')}
            </p>
          </div>
        </Dialog>
      )}
    </>
  );
}
